import { useEffect, useMemo, useState } from "react";
import { X, Loader2, ShoppingBag, CheckCircle2, AlertTriangle, MapPin } from "lucide-react";
import { supabase, STOCK_VIEW } from "@/lib/supabase";
import { toast } from "sonner";

const RESERVATIONS_TABLE = "website_reservations";

function normalizeStatus(raw) {
  const v = String(raw || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (v === "in_stock" || v === "available" || v === "in") return "in_stock";
  if (v === "low_stock" || v === "low") return "low_stock";
  if (v === "out_of_stock" || v === "out" || v === "unavailable" || v === "sold_out") return "out_of_stock";
  return null;
}

const STATUS_LABEL = { in_stock: "In stock", low_stock: "Low stock" };

const EMPTY_FORM = { name: "", phone: "", email: "", branchKey: "", qty: 1, notes: "" };

export default function ReserveDialog({ open, onClose, sku, productTitle }) {
  const [phase, setPhase] = useState("form"); // form | success
  const [loadingStock, setLoadingStock] = useState(true);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Fetch live stock on open, filter to reservable branches
  useEffect(() => {
    if (!open) return;
    setPhase("form");
    setForm(EMPTY_FORM);
    if (!sku || !supabase) {
      setRows([]);
      setLoadingStock(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStock(true);
      const { data, error } = await supabase
        .from(STOCK_VIEW)
        .select("product_id,sku,barcode,name,branch_id,branch_label,stock_status,public_quantity,available_quantity")
        .eq("sku", sku);
      if (cancelled) return;
      if (error) {
        toast.error("Couldn't load live stock. Please try again.");
        setRows([]);
        setLoadingStock(false);
        return;
      }
      const reservable = (data || []).filter((r) => {
        const s = normalizeStatus(r.stock_status);
        // Both checks: status must be in/low-stock AND there must be real availability left
        return (s === "in_stock" || s === "low_stock") && Number(r.available_quantity) > 0;
      });
      setRows(reservable);
      setForm((f) => ({ ...f, branchKey: reservable[0] ? branchKey(reservable[0]) : "" }));
      setLoadingStock(false);
    })();
    return () => { cancelled = true; };
  }, [open, sku]);

  const branches = useMemo(() => rows.map((r) => ({ key: branchKey(r), row: r, status: normalizeStatus(r.stock_status) })), [rows]);
  const selected = branches.find((b) => b.key === form.branchKey);
  const maxQty = selected ? Number(selected.row.available_quantity) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Please choose a branch."); return; }
    const qty = Math.max(1, Number(form.qty) || 1);
    if (maxQty && qty > maxQty) { toast.error(`Only ${maxQty} available at ${selected.row.branch_label}.`); return; }
    setSubmitting(true);
    const now = new Date();
    const payload = {
      id: (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      product_id: selected.row.product_id ?? null,
      sku: selected.row.sku,
      barcode: selected.row.barcode ?? null,
      product_name: selected.row.name || productTitle || null,
      branch_id: selected.row.branch_id ?? null,
      branch_label: selected.row.branch_label,
      customer_name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      qty,
      status: "pending",
      source: "website",
      notes: form.notes.trim() || null,
      created_at: now.toISOString(),
      updated_at: now.toISOString(),
      expires_at: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
    };
    let errorMsg = null;
    try {
      const { error } = await supabase.from(RESERVATIONS_TABLE).insert(payload);
      if (error) errorMsg = error.message || "Could not submit reservation.";
    } catch (e) {
      errorMsg = e?.message?.includes("body stream") || e?.message?.includes("schema cache")
        ? "Reservations are not configured yet. Please call us to reserve."
        : (e?.message || "Could not submit reservation.");
    }
    setSubmitting(false);
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }
    setPhase("success");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="reserve-dialog-backdrop">
      <div className="bg-white rounded-2xl w-full max-w-md my-10 p-8" onClick={(e) => e.stopPropagation()} data-testid="reserve-dialog">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline text-[#1a2d5e]">Click &amp; Collect</div>
            <div className="font-display font-black text-2xl text-[#0b1838] mt-1">Reserve this item</div>
            {productTitle && <div className="text-sm text-slate-500 mt-1 truncate max-w-[20rem]">{productTitle}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="reserve-close"><X size={20} /></button>
        </div>

        {phase === "success" ? (
          <div className="text-center py-6" data-testid="reserve-success">
            <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
            <div className="font-display font-bold text-xl mt-4 text-[#0b1838]">Reservation request sent.</div>
            <p className="text-slate-600 mt-2 text-sm">Staff will confirm by phone before collection.</p>
            <p className="text-slate-400 text-xs mt-3">Your reservation is held for up to 24 hours.</p>
            <button onClick={onClose} data-testid="reserve-done" className="mt-6 px-6 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838]">Done</button>
          </div>
        ) : loadingStock ? (
          <div className="py-10 text-center text-slate-500 flex items-center justify-center gap-2" data-testid="reserve-loading">
            <Loader2 className="animate-spin" size={16} /> Checking live availability…
          </div>
        ) : branches.length === 0 ? (
          <div className="py-6 text-center" data-testid="reserve-unavailable">
            <AlertTriangle className="mx-auto text-amber-500" size={40} />
            <div className="font-display font-bold text-lg mt-3 text-[#0b1838]">Currently unavailable</div>
            <p className="text-slate-600 mt-2 text-sm">No branch has this item in stock right now. Please call or message us — we may be able to source it.</p>
            <button onClick={onClose} className="mt-5 px-5 py-2 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4" data-testid="reserve-form">
            <div>
              <label className="overline text-[10px] block mb-2">Branch — choose pickup location</label>
              <div className="grid gap-2" role="radiogroup">
                {branches.map((b) => {
                  const active = b.key === form.branchKey;
                  return (
                    <button
                      type="button"
                      key={b.key}
                      onClick={() => setForm((f) => ({ ...f, branchKey: b.key, qty: 1 }))}
                      data-testid={`reserve-branch-${(b.row.branch_label || "").toLowerCase().replace(/\s+/g, "-")}`}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border-2 text-left transition-colors ${active ? "border-[#1a2d5e] bg-[#aedfff]/10" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPin size={16} className="text-[#1a2d5e] shrink-0" />
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0b1838] text-sm truncate">{b.row.branch_label}</div>
                          {b.row.public_quantity != null && b.row.public_quantity !== "" && (
                            <div className="text-[11px] text-slate-500">Qty available: {b.row.public_quantity}</div>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${b.status === "in_stock" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{STATUS_LABEL[b.status]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <input required placeholder="Your name" data-testid="reserve-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Phone" data-testid="reserve-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
              <input type="email" placeholder="Email (optional)" data-testid="reserve-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">Quantity{maxQty != null ? ` (max ${maxQty})` : ""}</label>
              <input required type="number" min={1} max={maxQty ?? undefined} data-testid="reserve-qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="w-28 px-3 py-3 border border-slate-300 rounded-lg" />
            </div>
            <textarea rows={2} placeholder="Notes (optional) — e.g. colour, pickup time" data-testid="reserve-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />

            <p className="text-xs text-slate-500">By submitting you authorise JAS staff to call you on the number above to confirm pickup. Reservation expires 24 hours after request.</p>

            <button type="submit" disabled={submitting} data-testid="reserve-submit" className="px-6 py-4 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : <><ShoppingBag size={16} /> Send reservation request</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function branchKey(row) {
  return `${row.branch_id ?? "_"}__${row.branch_label}`;
}
